import { isDefined } from "@clipboard-health/util-ts";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemButton,
  ListItemIcon,
  Stack,
  Tooltip,
} from "@mui/material";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { Pill } from "@src/appV2/redesign/components/Pill";
import { Text } from "@src/appV2/redesign/components/Text";
import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

import { getWorkerStageChipStyle } from "../utils/getWorkerStageChipStyle";

interface AgentResultItemProps {
  agent: Agent;
  selected: boolean;
  expanded: boolean;
  isLoadingShifts: boolean;
  copied?: boolean;
  onClick: () => void;
}

export const AgentResultItem = forwardRef<HTMLDivElement, AgentResultItemProps>((props, ref) => {
  const {
    agent,
    selected,
    expanded,
    isLoadingShifts,
    copied: copiedFromKeyboard = false,
    onClick,
  } = props;
  const [copiedFromClick, setCopiedFromClick] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    },
    []
  );

  const isCopied = copiedFromKeyboard || copiedFromClick;

  const handleCopy = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      navigator.clipboard.writeText(agent.userId).then(
        () => {
          if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
          }
          setCopiedFromClick(true);
          timerRef.current = setTimeout(() => {
            setCopiedFromClick(false);
          }, 2000);
        },
        () => {
          showErrorToast("Failed to copy to clipboard");
        }
      );
    },
    [agent.userId]
  );

  const stageLabel = agent.substage?.name ? `${agent.stage} · ${agent.substage.name}` : agent.stage;

  const secondaryParts = [
    agent.email,
    agent.metropolitanStatisticalArea,
    agent.qualification,
    agent.phone,
  ].filter(isDefined);

  return (
    <ListItemButton
      ref={ref}
      selected={selected}
      sx={(theme) => ({
        paddingY: 3,
        paddingX: 4,
        gap: 3,
        alignItems: "flex-start",
        "&.Mui-selected": {
          backgroundColor: theme.palette.action.selected,
        },
      })}
      onClick={onClick}
    >
      <ListItemIcon sx={{ minWidth: 0, marginTop: "2px" }}>
        <PersonIcon color="primary" fontSize="small" />
      </ListItemIcon>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" sx={{ alignItems: "center", gap: 2, flexWrap: "wrap", rowGap: 1 }}>
          <Text inline semibold sx={{ minWidth: 0 }}>
            {agent.name}
          </Text>
          <Pill
            size="small"
            sx={(theme) => ({
              borderWidth: "1px",
              paddingY: "1px",
              paddingX: "6px",
              fontSize: "0.625rem",
              lineHeight: 1.4,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              ...getWorkerStageChipStyle(agent.stage, theme),
            })}
          >
            {stageLabel}
          </Pill>
          {selected &&
            (expanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            ))}
          {isLoadingShifts && expanded && <CircularProgress size={14} />}
        </Stack>
        {secondaryParts.length > 0 && (
          <Text
            color="text.secondary"
            sx={{
              marginTop: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            variant="caption"
          >
            {secondaryParts.join(" · ")}
          </Text>
        )}
      </Box>
      <Tooltip title={isCopied ? "Copied!" : "Copy ID"}>
        <IconButton size="small" color={isCopied ? "success" : "default"} onClick={handleCopy}>
          {isCopied ? (
            <CheckIcon sx={{ fontSize: 16 }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Tooltip>
    </ListItemButton>
  );
});

AgentResultItem.displayName = "AgentResultItem";
